import { AtcStation, AtcScenario, getStationDisplayNameWithAirportName } from '@/types/AtcManifest';
import type {
  ApiAtcStationInfo,
  ApiAtcMainManifest,
  ApiAtcAirportInfo,
  ApiAtcScenario,
  ApiAtcStationManifest,
} from '../schemas/AtcManifestSchemas';

/**
 * Mappers for transforming API data to internal domain entities
 * 
 * Clean Architecture principle: Adapters translate between external
 * models and internal domain entities. This keeps the domain pure
 * and decoupled from external API formats.
 * 
 * These mappers:
 * - Convert snake_case API fields to camelCase domain fields
 * - Normalize and sanitize data (trim whitespace, consistent casing)
 * - Handle edge cases gracefully
 * - Keep domain entities clean and valid
 */

// ============================================================================
// Station Mapping
// ============================================================================

/**
 * Normalize airport code to uppercase and trimmed
 */
function normalizeAirportCode(code: string): string {
  return code.trim().toUpperCase();
}

/**
 * Normalize station type to lowercase and trimmed
 */
function normalizeStationType(type: string): string {
  return type.trim().toLowerCase();
}

/**
 * Capitalize first letter of station type for display
 */
function formatStationTypeForDisplay(type: string): string {
  const normalized = normalizeStationType(type);
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

type AirportMetadataContext = {
  airportName?: string | null;
  iataCode?: string | null;
  icaoCode?: string | null;
  localCode?: string | null;
  city?: string | null;
  country?: string | null;
  timeZone?: string | null;
};

function extractAirportMetadataFromStation(
  station: ApiAtcStationInfo
): AirportMetadataContext {
  return {
    airportName: station.airport_name ?? null,
    iataCode: station.iata_code ?? null,
    icaoCode: station.icao_code ?? null,
    localCode: station.local_code ?? null,
    city: station.city ?? null,
    country: station.country ?? null,
    timeZone: station.time_zone ?? null,
  };
}

function extractAirportMetadataFromAirport(
  airport: ApiAtcAirportInfo
): AirportMetadataContext {
  return {
    airportName: airport.name,
    iataCode: airport.iata_code,
    icaoCode: airport.icao_code,
    localCode: airport.local_code,
    city: airport.city,
    country: airport.country,
    timeZone: airport.time_zone,
  };
}

/**
 * Maps an API station info object to an internal AtcStation domain entity
 * 
 * @param apiStation - The API station info from the manifest
 * @param manifestUpdatedAt - The manifest's updated_at timestamp
 * @returns AtcStation domain entity
 */
export function mapApiStationToInternal(
  apiStation: ApiAtcStationInfo,
  manifestUpdatedAt: string,
  airportMetadata: AirportMetadataContext = {}
): AtcStation {
  const airportCode = normalizeAirportCode(apiStation.airport_code);
  const stationType = normalizeStationType(apiStation.station_type);

  const stationMetadata = extractAirportMetadataFromStation(apiStation);
  const mergedMetadata: AirportMetadataContext = {
    airportName: airportMetadata.airportName ?? stationMetadata.airportName ?? null,
    iataCode: airportMetadata.iataCode ?? stationMetadata.iataCode ?? null,
    icaoCode:
      airportMetadata.icaoCode ??
      stationMetadata.icaoCode ??
      airportCode,
    localCode: airportMetadata.localCode ?? stationMetadata.localCode ?? null,
    city: airportMetadata.city ?? stationMetadata.city ?? null,
    country: airportMetadata.country ?? stationMetadata.country ?? null,
    timeZone: airportMetadata.timeZone ?? stationMetadata.timeZone ?? null,
  };

  return {
    airportCode,
    stationType,
    displayName: getStationDisplayNameWithAirportName(
      airportCode,
      formatStationTypeForDisplay(stationType),
      mergedMetadata.airportName
    ),
    scenarioCount: apiStation.scenario_count,
    totalSizeBytes: apiStation.total_size_bytes,
    manifestUrl: apiStation.manifest_url.trim(),
    scenarios: null, // Not loaded yet
    updatedAt: manifestUpdatedAt,
    airportName: mergedMetadata.airportName,
    iataCode: mergedMetadata.iataCode,
    icaoCode: mergedMetadata.icaoCode,
    localCode: mergedMetadata.localCode,
    city: mergedMetadata.city,
    country: mergedMetadata.country,
    timeZone: mergedMetadata.timeZone,
    radioEffectPreset: apiStation.radio_effect_preset ?? undefined,
  };
}

/**
 * Maps the full main manifest to an array of AtcStation domain entities
 * 
 * @param manifest - The validated main manifest from the API
 * @returns Array of AtcStation domain entities
 */
export function mapApiMainManifestToStations(manifest: ApiAtcMainManifest): AtcStation[] {
  const stationsByKey = new Map<string, AtcStation>();

  const stationKey = (station: AtcStation) =>
    `${station.airportCode.toUpperCase()}::${station.stationType.toLowerCase()}`;

  const addStation = (station: AtcStation) => {
    const key = stationKey(station);
    if (!stationsByKey.has(key)) {
      stationsByKey.set(key, station);
    }
  };

  if ('airports' in manifest && Array.isArray(manifest.airports)) {
    for (const airport of manifest.airports) {
      const airportMetadata = extractAirportMetadataFromAirport(airport);
      for (const stationInfo of airport.stations) {
        addStation(
          mapApiStationToInternal(stationInfo, manifest.updated_at, airportMetadata)
        );
      }
    }
  }

  if ('stations' in manifest && Array.isArray(manifest.stations)) {
    for (const stationInfo of manifest.stations) {
      addStation(mapApiStationToInternal(stationInfo, manifest.updated_at));
    }
  }

  return Array.from(stationsByKey.values());
}

// ============================================================================
// Scenario Mapping
// ============================================================================

/**
 * Maps an API scenario object to an internal AtcScenario domain entity
 * 
 * Note: Currently the domain entity uses the same structure,
 * but this mapper exists to:
 * 1. Provide a clear boundary between API and domain
 * 2. Allow future domain model changes without affecting API parsing
 * 3. Normalize/sanitize data if needed
 * 
 * @param apiScenario - The API scenario from the station manifest
 * @returns AtcScenario domain entity
 */
export function mapApiScenarioToInternal(apiScenario: ApiAtcScenario): AtcScenario {
  return {
    scenario_id: apiScenario.scenario_id.trim(),
    size_bytes: apiScenario.size_bytes,
    uploaded_at: apiScenario.uploaded_at,
    cdn_url: apiScenario.cdn_url.trim(),
  };
}

/**
 * Maps an array of API scenarios to internal AtcScenario domain entities
 * 
 * @param apiScenarios - Array of API scenarios from the station manifest
 * @returns Array of AtcScenario domain entities
 */
export function mapApiScenariosToInternal(apiScenarios: ApiAtcScenario[]): AtcScenario[] {
  return apiScenarios.map(mapApiScenarioToInternal);
}

// ============================================================================
// Station Manifest Mapping
// ============================================================================

/**
 * Result of mapping a station manifest
 */
export interface MappedStationManifest {
  airportCode: string;
  stationType: string;
  scenarios: AtcScenario[];
  totalSizeBytes: number;
  scenarioCount: number;
  updatedAt: string;
}

/**
 * Maps an API station manifest to internal domain representation
 * 
 * @param apiManifest - The validated station manifest from the API
 * @returns Mapped station manifest data
 */
export function mapApiStationManifestToInternal(
  apiManifest: ApiAtcStationManifest
): MappedStationManifest {
  return {
    airportCode: normalizeAirportCode(apiManifest.airport_code),
    stationType: normalizeStationType(apiManifest.station_type),
    scenarios: mapApiScenariosToInternal(apiManifest.scenarios),
    totalSizeBytes: apiManifest.total_size_bytes,
    scenarioCount: apiManifest.scenario_count,
    updatedAt: apiManifest.updated_at,
  };
}

/**
 * Updates an existing AtcStation with loaded scenarios from a station manifest
 * 
 * @param station - The existing station entity
 * @param stationManifest - The loaded station manifest data
 * @returns Updated AtcStation with scenarios populated
 */
export function mergeStationWithManifest(
  station: AtcStation,
  stationManifest: MappedStationManifest
): AtcStation {
  return {
    ...station,
    scenarios: stationManifest.scenarios,
    scenarioCount: stationManifest.scenarioCount,
    totalSizeBytes: stationManifest.totalSizeBytes,
    updatedAt: stationManifest.updatedAt,
  };
}
